
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import dataManager from '../utils/dataManager';
import { API_ENDPOINTS } from '../config/appConfig';
import { getAuthHeaders } from '../utils/authApi';
import ConfigService from '../utils/configService';
import './PhilosophyDetail.css';

const PhilosophyDetail = () => {
  const { user } = useAuth();
  const [selectedPhilosophy, setSelectedPhilosophy] = useState(null);
  const [philosophyReflections, setPhilosophyReflections] = useState({});
  const [newReflection, setNewReflection] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [philosophies, setPhilosophies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // 加载哲学数据
  useEffect(() => {
    const loadPhilosophies = async () => {
      try {
        setLoading(true);
        setError(null);
        const philosophiesData = await dataManager.getPracticePhilosophy();
        setPhilosophies(Array.isArray(philosophiesData) ? philosophiesData : []);
      } catch (err) {
        console.error('Error loading philosophies:', err);
        setError('加载哲学原则失败，请确保Django服务器正在运行');
        // 降级到本地数据
        const fallbackData = dataManager.getPracticePhilosophySync() || [];
        setPhilosophies(fallbackData);
      } finally {
        setLoading(false);
      }
    };

    loadPhilosophies();
  }, []);

  // 加载哲学反思数据
  useEffect(() => {
    const loadPhilosophyReflections = async () => {
      if (ConfigService.useServerData()) {
        if (philosophies.length === 0) return;

        try {
          const entries = await Promise.all(
            philosophies.map(async (philosophy) => {
              const reflections = await dataManager.getPhilosophyReflectionsByName(philosophy.name);
              return [philosophy.name, Array.isArray(reflections) ? reflections.map(formatPhilosophyReflection) : []];
            })
          );
          setPhilosophyReflections(Object.fromEntries(entries));
        } catch (err) {
          console.error('Error loading philosophy reflections:', err);
          setPhilosophyReflections({});
        }
        return;
      }

      const saved = localStorage.getItem('philosophyReflections');
      if (saved) {
        try {
          const parsedData = JSON.parse(saved);
          // 检查数据是否包含所有哲学原则
          const requiredPhilosophies = ['Find your breath', 'Trust the process', 'Practice with love', 'Embrace the journey'];
          const hasAllPhilosophies = requiredPhilosophies.every(p => parsedData[p] && parsedData[p].length > 0);

          if (hasAllPhilosophies) {
            setPhilosophyReflections(parsedData);
            return;
          }
        } catch (error) {
          console.log('Error parsing saved reflections, loading fresh data');
        }
      }

      // 从dataManager中加载初始反思数据
      const initialReflections = dataManager.getPhilosophyReflections();
      setPhilosophyReflections(initialReflections);
      localStorage.setItem('philosophyReflections', JSON.stringify(initialReflections));
    };

    loadPhilosophyReflections();
  }, [philosophies]);

  const handlePhilosophyClick = (philosophy) => {
    setSelectedPhilosophy(selectedPhilosophy?.name === philosophy.name ? null : philosophy);
    setShowAddForm(false);
    setNewReflection('');
  };

  const formatPhilosophyReflection = (item) => ({
    id: item.id,
    content: item.content,
    author: item.author,
    date: item.created_at || item.date,
    likes: item.likes_count ?? item.likes ?? 0,
    tags: item.tags || [],
    isLiked: item.is_liked || false,
  });

  const getErrorMessage = (data, fallback) => {
    if (!data || typeof data !== 'object') return fallback;
    if (data.detail) return data.detail;
    if (data.non_field_errors?.[0]) return data.non_field_errors[0];

    const firstKey = Object.keys(data)[0];
    const firstValue = firstKey ? data[firstKey] : null;
    if (Array.isArray(firstValue) && firstValue[0]) {
      return `${firstKey}: ${firstValue[0]}`;
    }
    return fallback;
  };

  const handleAddReflection = async (philosophyName) => {
    if (!user) {
      alert('请先登录才能添加反思');
      return;
    }

    if (!newReflection.trim()) {
      alert('请输入反思内容');
      return;
    }

    try {
      setSubmitting(true);

      if (ConfigService.useServerData()) {
        const philosophy = philosophies.find(item => item.name === philosophyName);
        if (!philosophy) {
          throw new Error('未找到对应的哲学原则');
        }

        const response = await fetch(ConfigService.getApiUrl(API_ENDPOINTS.PHILOSOPHY_REFLECTIONS), {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            philosophy_id: philosophy.id,
            content: newReflection.trim(),
            tags: [],
          }),
        });
        const responseData = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(getErrorMessage(responseData, '发布反思失败'));
        }

        const refreshedReflections = await dataManager.getPhilosophyReflectionsByName(philosophyName);
        setPhilosophyReflections(prev => ({
          ...prev,
          [philosophyName]: Array.isArray(refreshedReflections)
            ? refreshedReflections.map(formatPhilosophyReflection)
            : [],
        }));
      } else {
        const reflection = {
          id: Date.now(),
          content: newReflection.trim(),
          author: user.username,
          date: new Date().toISOString().split('T')[0],
          likes: 0
        };

        const updatedReflections = {
          ...philosophyReflections,
          [philosophyName]: [
            reflection,
            ...(philosophyReflections[philosophyName] || [])
          ]
        };

        setPhilosophyReflections(updatedReflections);
        localStorage.setItem('philosophyReflections', JSON.stringify(updatedReflections));
      }

      setNewReflection('');
      setShowAddForm(false);
    } catch (err) {
      console.error('Error saving philosophy reflection:', err);
      alert(err.message || '发布反思失败，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLike = (philosophyName, reflectionId) => {
    const updatedReflections = {
      ...philosophyReflections,
      [philosophyName]: philosophyReflections[philosophyName]?.map(reflection =>
        reflection.id === reflectionId
          ? { ...reflection, likes: reflection.likes + 1 }
          : reflection
      ) || []
    };

    setPhilosophyReflections(updatedReflections);
    localStorage.setItem('philosophyReflections', JSON.stringify(updatedReflections));
  };

  // 加载状态
  if (loading) {
    return (
      <div className="philosophy-detail">
        <div className="philosophy-header">
          <h2>🌟 练习哲学</h2>
          <div className="loading-state">
            <div className="loading-spinner">🧘‍♀️</div>
            <p>正在加载哲学原则...</p>
          </div>
        </div>
      </div>
    );
  }

  // 错误状态
  if (error) {
    return (
      <div className="philosophy-detail">
        <div className="philosophy-header">
          <h2>🌟 练习哲学</h2>
          <div className="error-state">
            <div className="error-icon">⚠️</div>
            <p>{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="retry-btn"
            >
              重新加载
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="philosophy-detail">
      <div className="philosophy-header">
        <h2>🌟 练习哲学</h2>
        <p>探索阿斯汤加瑜伽的核心智慧，让古老的教导指引你的修行之路</p>
      </div>

      <div className="philosophy-grid">
        {philosophies.map((philosophy, index) => (
          <div key={index} className="philosophy-card">
            <div 
              className="philosophy-summary"
              onClick={() => handlePhilosophyClick(philosophy)}
            >
              <div className="philosophy-icon">
                <span className="icon">{philosophy.icon}</span>
              </div>
              <div className="philosophy-basic">
                <h3>{philosophy.name}</h3>
                <p className="philosophy-description">{philosophy.description}</p>
              </div>
              <div className="expand-icon">
                {selectedPhilosophy?.name === philosophy.name ? '▼' : '▶'}
              </div>
            </div>

            {selectedPhilosophy?.name === philosophy.name && (
              <div className="philosophy-details">
                <div className="detailed-content">
                  <h4>📖 深入理解</h4>
                  <div className="details-text">
                    {philosophy.details ? philosophy.details.split('\n\n').map((paragraph, pIndex) => (
                      <p key={pIndex}>{paragraph}</p>
                    )) : null}
                  </div>
                </div>

                {philosophy.practices && Array.isArray(philosophy.practices) && (
                  <div className="practice-suggestions">
                    <h4>🎯 实践建议</h4>
                    <ul>
                      {philosophy.practices.map((practice, pIndex) => (
                        <li key={pIndex}>{practice}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* 社区反思列表 - 根据哲学原则定制 */}
                <div className={`community-reflections-section philosophy-${philosophy.name.toLowerCase().replace(/\s+/g, '-')}`}>
                  <div className="reflections-header">
                    <h4>{getPhilosophyIcon(philosophy.name)} {getPhilosophyTitle(philosophy.name)}</h4>
                    <p className="text-sm text-gray-600 mb-4">
                      {getPhilosophyDescription(philosophy.name)}
                    </p>
                    {user && (
                      <button
                        onClick={() => setShowAddForm(!showAddForm)}
                        className={`add-reflection-btn ${getPhilosophyButtonClass(philosophy.name)}`}
                      >
                        {showAddForm ? '取消分享' : `分享我的${getPhilosophyReflectionType(philosophy.name)}`}
                      </button>
                    )}
                    {!user && (
                      <div className={`login-prompt ${getPhilosophyPromptClass(philosophy.name)}`}>
                        <p>登录后可以分享您对"{philosophy.name}"的{getPhilosophyReflectionType(philosophy.name)}</p>
                      </div>
                    )}
                  </div>

                  {/* 添加反思表单 */}
                  {showAddForm && user && (
                    <div className="add-reflection-form">
                      <textarea
                        value={newReflection}
                        onChange={(e) => setNewReflection(e.target.value)}
                        placeholder="分享您对这个哲学原则的理解和感悟..."
                        className="reflection-textarea"
                        rows="4"
                      />
                      <div className="form-actions">
                        <button
                          onClick={() => handleAddReflection(philosophy.name)}
                          className="submit-reflection-btn"
                          disabled={submitting || !newReflection.trim()}
                        >
                          {submitting ? '发布中...' : '发布反思'}
                        </button>
                        <button
                          onClick={() => {
                            setShowAddForm(false);
                            setNewReflection('');
                          }}
                          className="cancel-btn"
                        >
                          取消
                        </button>
                      </div>
                    </div>
                  )}

                  {/* 反思列表 - 定制化显示 */}
                  <div className="reflections-list">
                    {philosophyReflections[philosophy.name]?.length > 0 ? (
                      philosophyReflections[philosophy.name].map((reflection) => (
                        <div key={reflection.id} className={`reflection-item ${getReflectionItemClass(philosophy.name)}`}>
                          <div className="reflection-header">
                            <div className="author-info">
                              <div className="author-avatar">
                                {getAuthorAvatar(reflection.author)}
                              </div>
                              <div className="author-details">
                                <span className="author-name">
                                  {typeof reflection.author === 'object' ?
                                    (reflection.author.name || reflection.author.username || '匿名用户') :
                                    reflection.author || '匿名用户'}
                                </span>
                                <span className="reflection-date">{formatDate(reflection.date)}</span>
                              </div>
                            </div>
                            <div className="reflection-actions">
                              <button
                                onClick={() => handleLike(philosophy.name, reflection.id)}
                                className={`like-btn ${getLikeButtonClass(philosophy.name)}`}
                              >
                                ❤️ {reflection.likes}
                              </button>
                            </div>
                          </div>
                          <div className="reflection-content">
                            {reflection.content}
                          </div>
                          {reflection.tags && Array.isArray(reflection.tags) && (
                            <div className="reflection-tags">
                              {reflection.tags.map((tag, tagIndex) => (
                                <span key={tagIndex} className={`tag ${getTagClass(philosophy.name)}`}>
                                  #{tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className={`no-reflections ${getEmptyStateClass(philosophy.name)}`}>
                        <div className="empty-icon">{getEmptyStateIcon(philosophy.name)}</div>
                        <h5>还没有{getPhilosophyReflectionType(philosophy.name)}</h5>
                        <p>
                          {user
                            ? `成为第一个分享${getPhilosophyReflectionType(philosophy.name)}的人吧！`
                            : `登录后可以分享您对"${philosophy.name}"的理解。`
                          }
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="philosophy-footer">
        <div className="wisdom-quote">
          <h3>🕉️ 古老的智慧</h3>
          <blockquote>
            "瑜伽是心灵波动的止息。当心灵的波动停止时，观察者就安住在自己的本性中。"
            <cite>— 《瑜伽经》1.2-1.3</cite>
          </blockquote>
        </div>
        
        <div className="philosophy-resources">
          <h3>📚 延伸阅读</h3>
          <div className="resource-links">
            <a href="#" className="resource-link">
              📖 《瑜伽经》完整版
            </a>
            <a href="#" className="resource-link">
              📖 《薄伽梵歌》精选章节
            </a>
            <a href="#" className="resource-link">
              📖 阿斯汤加瑜伽传统教学
            </a>
            <a href="#" className="resource-link">
              🎥 哲学讲座视频
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

// 辅助函数 - 为不同哲学原则定制界面
const getPhilosophyIcon = (philosophyName) => {
  const icons = {
    'Find your breath': '🫁',
    'Trust the process': '🌱',
    'Practice with love': '💚',
    'Embrace the journey': '🛤️'
  };
  return icons[philosophyName] || '💭';
};

const getPhilosophyTitle = (philosophyName) => {
  const titles = {
    'Find your breath': '呼吸感悟',
    'Trust the process': '过程体验',
    'Practice with love': '爱的练习',
    'Embrace the journey': '旅程分享'
  };
  return titles[philosophyName] || '社区反思';
};

const getPhilosophyDescription = (philosophyName) => {
  const descriptions = {
    'Find your breath': '分享您在呼吸练习中的发现和感悟',
    'Trust the process': '记录您在练习过程中的耐心与成长',
    'Practice with love': '分享如何在练习中培养自我慈悲',
    'Embrace the journey': '讲述您瑜伽旅程中的美好时刻'
  };
  return descriptions[philosophyName] || '看看其他练习者对这个哲学原则的理解和感悟';
};

const getPhilosophyReflectionType = (philosophyName) => {
  const types = {
    'Find your breath': '呼吸体验',
    'Trust the process': '成长感悟',
    'Practice with love': '爱的故事',
    'Embrace the journey': '旅程故事'
  };
  return types[philosophyName] || '反思';
};

const getPhilosophyButtonClass = (philosophyName) => {
  const classes = {
    'Find your breath': 'breath-theme',
    'Trust the process': 'process-theme',
    'Practice with love': 'love-theme',
    'Embrace the journey': 'journey-theme'
  };
  return classes[philosophyName] || '';
};

const getPhilosophyPromptClass = (philosophyName) => {
  const classes = {
    'Find your breath': 'breath-prompt',
    'Trust the process': 'process-prompt',
    'Practice with love': 'love-prompt',
    'Embrace the journey': 'journey-prompt'
  };
  return classes[philosophyName] || '';
};

const getReflectionItemClass = (philosophyName) => {
  const classes = {
    'Find your breath': 'breath-reflection',
    'Trust the process': 'process-reflection',
    'Practice with love': 'love-reflection',
    'Embrace the journey': 'journey-reflection'
  };
  return classes[philosophyName] || '';
};

const getLikeButtonClass = (philosophyName) => {
  const classes = {
    'Find your breath': 'breath-like',
    'Trust the process': 'process-like',
    'Practice with love': 'love-like',
    'Embrace the journey': 'journey-like'
  };
  return classes[philosophyName] || '';
};

const getTagClass = (philosophyName) => {
  const classes = {
    'Find your breath': 'breath-tag',
    'Trust the process': 'process-tag',
    'Practice with love': 'love-tag',
    'Embrace the journey': 'journey-tag'
  };
  return classes[philosophyName] || '';
};

const getEmptyStateClass = (philosophyName) => {
  const classes = {
    'Find your breath': 'breath-empty',
    'Trust the process': 'process-empty',
    'Practice with love': 'love-empty',
    'Embrace the journey': 'journey-empty'
  };
  return classes[philosophyName] || '';
};

const getEmptyStateIcon = (philosophyName) => {
  const icons = {
    'Find your breath': '🫁',
    'Trust the process': '🌱',
    'Practice with love': '💚',
    'Embrace the journey': '🛤️'
  };
  return icons[philosophyName] || '💭';
};

const getAuthorAvatar = (author) => {
  // 确保 authorName 是字符串
  const authorName = typeof author === 'object' ?
    (author.name || author.username || '匿名') :
    (author || '匿名');

  // 根据作者姓名生成简单的头像
  const firstChar = authorName.charAt(0);
  const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8'];
  const colorIndex = authorName.length % colors.length;

  return (
    <div
      className="avatar-circle"
      style={{ backgroundColor: colors[colorIndex] }}
    >
      {firstChar}
    </div>
  );
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now - date);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 1) return '昨天';
  if (diffDays === 2) return '前天';
  if (diffDays <= 7) return `${diffDays}天前`;

  return date.toLocaleDateString('zh-CN', {
    month: 'short',
    day: 'numeric'
  });
};

export default PhilosophyDetail;
